/**
 * Outreach v2 — the snake_case wire shapes the BE (discovr-backend
 * internal/outreach) actually sends, and the explicit wire<->FE mappers.
 * Split out of outreachApi.ts, which keeps the route surface: this file is
 * pure shape translation with no HTTP in it, and nothing outside
 * outreachApi.ts should import from here.
 */
import type {
  CreatorLookupStatus,
  MetricsStatus,
  OutreachBrandState,
  OutreachCounts,
  OutreachProfileGeo,
  OutreachRow,
  OutreachRowLastMessage,
  OutreachRowMailbox,
  OutreachContactOutcome,
  OutreachFirstTouch,
  OutreachFollowup,
  OutreachWaitingSend,
  OutreachRowStatus,
  OutreachState,
  OutreachEmailSource,
  OutreachRowEmail,
  OutreachImportBatch,
  OutreachHandlePreview,
  OutreachActiveOperation,
  OutreachBulkSend,
  OutreachFirstTouchDrafts,
  OutreachOperation,
  OutreachContactPatch,
  OutreachFillStepResult,
  OutreachTemplate,
  OutreachTemplateKind,
  OutreachTemplateLintWarning,
  OutreachTemplateScope,
  OutreachTemplatePreview,
  OutreachTemplateStep,
  OutreachSignatureSource,
  OutreachSettings,
  OutreachSettingsInput,
  OutreachEntryEvent,
  OutreachMessage,
  OutreachMessageStatus,
  OutreachQueuedMessage,
  OutreachSendStatus,
  OutreachPoolMailbox,
  OutreachImportSource,
  OutreachDraft,
  OutreachSuggestion,
  OutreachTemplateStepText,
  OutreachParsed,
  OutreachParsedBundle,
  OutreachReplyChip,
  OutreachReplyChipAction,
} from '@discovr/types';
import type { ConvertedMoney, RowMoney } from '@discovr/types';
import { DEFAULT_OUTREACH_REGION, OUTREACH_STATES, OutreachWireError, isOutreachAudienceSource, isOutreachContactOutcome, isOutreachFirstTouchStatus, isOutreachRowStatus, isOutreachState, isPastOutreachStatus } from '@discovr/types';
import { booleanOrNull, countOrNull } from './outreachWireValues';

// ── Wire shapes (snake_case JSON) ─────────────────────────────────────────

/** Go RowEmail (views.go). */
export interface WireRowEmail {
  address: string;
  source: string;
  confidence: number;
  found_at?: string;
  primary: boolean;
}

export interface WireProfileGeo {
  country?: string;
  country_source?: string;
  timezone?: string;
  timezone_source?: string;
}

export interface WireOutreachRow {
  entry_id: string;
  user_id: string;
  username: string;
  display_name?: string;
  avatar_url?: string;
  followers?: number;
  followers_hidden?: boolean;
  avg_views?: number;
  engagement_rate?: number;
  lookup_status?: CreatorLookupStatus;
  lookup_checked_at?: string;
  metrics_status?: MetricsStatus;
  metrics_pending_since?: string;
  /** Roster status; `outreach` for the whole outreach phase. Sent as a string so the mapper can refuse an unknown one. */
  status: string;
  /** outreach.state (spec §2.1c). Required: a row without it is a contract break, not a default. */
  outreach_state: string;
  /** Go's domain.OutreachContact; only `status` is read. */
  outreach_contact?: { status?: string };
  first_touch?: { status?: string; scheduled_for?: string };
  email?: string;
  email_source?: OutreachEmailSource | (string & {});
  email_override?: string;
  emails?: WireRowEmail[];
  primary_pinned?: boolean;
  has_contact: boolean;
  send_tz?: string;
  profile_geo?: WireProfileGeo | null;
  scheduled_at?: string;
  last_activity_at: string;
  syncing: boolean;
  sync_stuck?: boolean;
  pinned_mailbox?: WireRowMailbox;
  next_send_at?: string;
  next_send_zone?: string;
  thread_tz?: string;
  next_followup_at?: string;
  last_message?: WireRowLastMessage;
  last_reply?: WireRowLastReply;
  open_quote_intent?: import('@discovr/types').OutreachIntent | null;
  needs_you?: boolean;
  awaiting_reply?: boolean;
  escalated_at?: string;
  escalation_reason?: string;
  // The negotiation-driven columns (Outreach v2 Part 2.5, ruling 69).
  followups_paused_at?: string;
  unread?: boolean;
  import_source?: string;
  agreed_deliverables?: WireDeliverableInstance[];
  /** Go's totals over agreed_deliverables (wire §2). */
  agreed_money?: RowMoney;
  platform?: string;
  contact_lookup_supported?: boolean;
  contact_lookup_note?: string;
  /** G4: the newest first touch is a draft parked without an address and the creator still has none. */
  draft_waiting_email?: boolean;
  /** G4c: the stored send choice of an approved draft that waits for an email. */
  waiting_send?: WireWaitingSend | null;
  suppressed?: boolean;
  suppression_scope?: 'global' | 'brand' | 'campaign';
  suppression_id?: number;
  not_unsubscribe?: 'reopen' | 'lift_only';
  followup_deferred?: { action: 'none' | 'hold' | 'nudge_after'; reason: string; not_before: string | null; scheduled_at: string | null } | null;
  followup?: WireRowFollowup | null;
  escalation_source?: 'rule' | 'agent' | '';
  escalation_note?: string;
  escalation_cleared_by?: string;
  escalation_cleared_at?: string;
  escalation_cleared_note?: string;
  held_reason?: string | null;
  hold_reason?: { code: string; label: string } | null;
  brand_state?: OutreachBrandState;
  handed_off_at?: string;
  brand_decided_at?: string;
  confirmed_at?: string;
  last_outbound_status?: OutreachMessageStatus;
  last_outbound_message_id?: number;
  /** CRM v2 (contract §3); absent from an older Go. */
  message_count?: number;
  audience_source?: string;
  has_quote?: boolean;
}

/** Mirrors the BE's domain.DeliverableInstance: base_price is in `currency` (INR when absent); `converted` is Go's INR figure at the current rate. */
export interface WireDeliverableInstance {
  type: string;
  base_price: number;
  currency?: string;
  converted?: ConvertedMoney;
  is_selected?: boolean;
  days?: number;
}

export interface WireRowMailbox {
  id: number;
  address: string;
  display_name: string;
}

export interface WireRowLastMessage {
  id: number;
  kind: string;
  status: OutreachMessageStatus;
  subject?: string;
  snippet?: string;
  held_reason?: string;
  last_error?: string;
  sent_at?: string;
}

/** Go RowFollowup (owner 2026-10-12): null when nothing is owed. */
export interface WireRowFollowup {
  kind: string;
  due_at: string;
  due: boolean;
  overdue_days?: number;
  // F4 (steps_remaining)
  next_at?: string;
  step?: number;
  state?: import('@discovr/types').OutreachAutoFollowupState;
  // state failed only
  reason?: string;
  retryable?: boolean;
  template_step?: number;
  attempts?: number;
}

export interface WireRowLastReply {
  id: number;
  received_at: string;
  intent?: import('@discovr/types').OutreachIntent;
  snippet?: string;
  is_auto_reply: boolean;
}

export interface WireOutreachCounts {
  by_state?: Partial<Record<OutreachState, number>>;
  with_brand?: number;
  removed_or_rejected?: number;
  scheduled?: number;
  /** On the wire only: the FE never reads it (P35, no Drafts panel). */
  drafts?: number;
  unread?: number;
  not_contacted?: number;
  in_conversation?: number;
  /** CRM v2 (contract §3); absent from an older Go. */
  conversations?: number;
  with_rates?: number;
  everyone?: number;
  needs_you?: number;
}

export interface WireImportBatchView {
  id: number;
  status: string;
  row_count: number;
  created_count: number;
  matched_count: number;
  skipped_count: number;
  failed_count?: number;
}

/** Go's previewResponse (internal/outreach/handler_campaign.go); a nil slice arrives as null. */
export interface WireHandlePreview {
  handles: { input: string; username: string; status: string; contact_email?: string }[] | null;
  counts: {
    total: number;
    ok: number;
    duplicate: number;
    invalid: number;
    already_in_campaign: number;
    has_price: number;
    wrong_platform: number;
  };
  file_token: string;
  commit_with_handles: boolean;
}

export function toHandlePreview(w: WireHandlePreview): OutreachHandlePreview {
  return {
    counts: {
      total: w.counts.total,
      ok: w.counts.ok,
      duplicate: w.counts.duplicate,
      invalid: w.counts.invalid,
      alreadyInCampaign: w.counts.already_in_campaign,
      hasPrice: w.counts.has_price,
      wrongPlatform: w.counts.wrong_platform,
    },
    committable: (w.handles ?? []).filter((row) => row.status === 'ok').map((row) => row.username),
    statuses: Object.fromEntries((w.handles ?? []).map((row) => [row.username.toLowerCase(), row.status])),
    contactEmails: Object.fromEntries(
      (w.handles ?? []).flatMap((row) => (row.contact_email ? [[row.username.toLowerCase(), row.contact_email]] : [])),
    ),
    fileToken: w.file_token,
    commitWithHandles: w.commit_with_handles,
  };
}

/** One outreach.import_items row (Go's importItemView, ImportGet's own
 *  `items[]` — internal/outreach/handler_campaign.go). `roster_entry_id` is
 *  only set once the item reached `created`/`matched`; omitted (empty
 *  string) for `pending`/`skipped_*`/`failed`. */
export interface WireImportItemView {
  id: number;
  input_raw: string;
  handle: string;
  state: 'pending' | 'created' | 'matched' | 'skipped_duplicate' | 'skipped_invalid' | 'skipped_has_price' | 'failed';
  roster_entry_id?: string;
  error?: string;
  processed_at?: string;
}

export interface WireOutreachOperation {
  operation_id: string;
  kind: string;
  status: string;
  total: number;
  applied_count: number;
  skipped_count: number;
  failed_count: number;
  outcomes: { item_key: string; outcome: 'applied' | 'skipped' | 'failed' | 'queued' | 'exists'; reason?: string; message_id?: number; retryable?: boolean }[];
}

export interface WireTemplateStep {
  id?: number;
  step_no: number;
  delay_days?: number;
  subject?: string;
  body: string;
  body_html?: string;
  send_time?: string;
  ai_assisted?: boolean;
  manual?: boolean;
}

export interface WireTemplateLintWarning {
  step_no: number;
  code: string;
  message: string;
}

export interface WireTemplate {
  id: number;
  scope: OutreachTemplateScope;
  brand_id?: string;
  campaign_id?: string;
  parent_template_id?: number;
  kind: OutreachTemplateKind;
  name: string;
  steps: WireTemplateStep[] | null;
  used_by: number;
  archived_at?: string;
  created_by: string;
  updated_at: string;
  version?: number;
  ai_assisted?: boolean;
  signature?: string | null;
  signature_html?: string;
  warnings?: WireTemplateLintWarning[] | null;
}

export interface WireTemplateGroups {
  global: WireTemplate[] | null;
  brand: WireTemplate[] | null;
  campaign: WireTemplate[] | null;
}

/** The BE also returns `vars` (the render context); the UI renders only the
 *  resolved subject/body, so it is deliberately not carried into the FE type. */
export interface WireTemplatePreview {
  subject: string;
  body_text: string;
  body_html?: string;
  signature_source?: OutreachSignatureSource;
}

export interface WireSettings {
  campaign_id: string;
  exists: boolean;
  sequence_template_id?: number;
  ask_rate_first: boolean;
  send_tz: string;
  walk_away_ceiling?: number;
  max_rounds: number;
  followup_daily_cap: number;
  /** F4: absent from an older Go, which sends nothing automatically, so read as ON. */
  auto_followups?: boolean;
  region?: string;
  region_set?: boolean;
  paused_at?: string;
  updated_at?: string;
  template_locked?: boolean;
}

export interface WireEntryEvent {
  id: number;
  kind: OutreachEntryEvent['kind'];
  actor: string;
  actor_kind?: OutreachEntryEvent['actorKind'];
  actor_name?: string;
  created_at: string;
  reason?: string;
  scope?: OutreachEntryEvent['scope'];
  scope_brand_name?: string;
}

export function toEntryEvent(w: WireEntryEvent): OutreachEntryEvent {
  return {
    id: w.id,
    kind: w.kind,
    actor: w.actor,
    ...(w.actor_kind ? { actorKind: w.actor_kind } : {}),
    ...(w.actor_name ? { actorName: w.actor_name } : {}),
    at: w.created_at,
    ...(w.reason ? { reason: w.reason } : {}),
    ...(w.scope ? { scope: w.scope } : {}),
    ...(w.scope_brand_name ? { scopeBrandName: w.scope_brand_name } : {}),
  };
}

export interface WireMessage {
  id: number;
  direction: 'out' | 'in';
  kind: string;
  step?: number;
  status: OutreachMessageStatus;
  subject?: string;
  body_text: string;
  to_address?: string;
  from_address?: string;
  mailbox_id?: number;
  requested_mailbox_id?: number;
  held_reason?: string;
  address_guard?: { code: string; label: string } | null;
  last_error?: string;
  draft_returned?: boolean;
  scheduled_for?: string;
  send_tz?: string;
  send_attempted_at?: string;
  sent_at?: string;
  received_at?: string;
  visible_text?: string;
  intent?: import('@discovr/types').OutreachIntent;
  parsed?: WireParsed;
  match_confidence?: import('@discovr/types').OutreachMatchConfidence;
  candidate_entry_id?: string;
  is_auto_reply?: boolean;
  is_dsn?: boolean;
  dsn?: WireDSN;
  truncated?: boolean;
  parse_error?: string;
  quote?: WireQuote;
  body_html?: string;
  body_rich_html?: string;
  attempts: number;
  actor: string;
  created_at: string;
  attachments?: WireAttachmentSummary[];
  draft_source?: 'agent' | 'template' | 'human' | 'fallback' | null;
  edited_before_approval?: boolean;
}

export interface WireAttachmentSummary {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  is_inline?: boolean;
}

export interface WireParsed {
  source: 'regex' | 'llm';
  rates: { deliverable: string; amount: number; currency: string; confidence: number; quote?: string; not_pitched?: boolean }[];
  unmatched?: { amount: number; currency: string; quote: string }[];
  /** The human string; old rows store only this. */
  bundle?: string;
  bundle_detail?: { deliverable_ids: string[]; amount: number; currency: string; per_unit?: boolean; items?: { deliverable_id: string; count: number; amount?: number | null }[] | null; free_ids?: string[] | null };
  phone?: string;
  notes?: string;
  suggestions?: { action: string; label: string; draft: { subject: string | null; body: string }; source: string }[];
}

export interface WireDSN { action: string; status: string; kind: 'hard' | 'soft' | 'blocked' | 'delayed' | 'other'; original_message_id?: string }
export interface WireQuote {
  id: number;
  round: number;
  rates: { deliverable: string; amount: number; currency: string; confidence: number; source: string }[];
  created_at: string;
  /** Go Task 22, omitempty. */
  human_round?: number;
}

export interface WireQueuedMessage {
  message_id: number;
  status: 'queued';
  scheduled_for?: string;
}

export interface WireSendStatus {
  enabled: boolean;
  send_hold: boolean;
  gmail_configured: boolean;
  mailbox_count: number;
  active_mailboxes: number;
  unmatched_count?: number;
}

/** The pool route answers the full admin MailboxView; only these fields are
 *  read here (the picker has no use for warmup/bounce/poll health). */
export interface WireMailboxView {
  id: number;
  address: string;
  display_name: string;
  status: string;
  sent_today: number;
  cap_today: number;
}

// ── Mappers ────────────────────────────────────────────────────────────────

export function toRowMailbox(w: WireRowMailbox): OutreachRowMailbox {
  return { id: w.id, address: w.address, displayName: w.display_name };
}

export function toRowLastMessage(w: WireRowLastMessage): OutreachRowLastMessage {
  return {
    id: w.id,
    kind: w.kind,
    status: w.status,
    subject: w.subject,
    snippet: w.snippet,
    heldReason: w.held_reason,
    lastError: w.last_error,
    sentAt: w.sent_at,
  };
}

/** Layer 2b is recorded, not load-bearing, so an unknown or blank status reads as unset rather than breaking the row. */
function contactOutcome(status: string | undefined): OutreachContactOutcome | undefined {
  return isOutreachContactOutcome(status) ? status : undefined;
}

/** An unknown status reads as "no first touch", so the row keeps the pre-#711 behaviour instead of guessing. */
function firstTouch(w: WireOutreachRow['first_touch']): OutreachFirstTouch | undefined {
  if (!w || !isOutreachFirstTouchStatus(w.status)) return undefined;
  return w.scheduled_for ? { status: w.status, scheduledFor: w.scheduled_for } : { status: w.status };
}

function validatedStatus(w: WireOutreachRow): { status: OutreachRowStatus; outreachState: OutreachState } {
  const known = isOutreachRowStatus(w.status);
  if (!known && !isPastOutreachStatus(w.status)) throw new OutreachWireError('status', w.status, w.entry_id);
  if (!isOutreachState(w.outreach_state)) throw new OutreachWireError('outreach_state', w.outreach_state, w.entry_id);
  // An unknown past-outreach status rides through as-is; the pill de-slugs it and styles it as With brand.
  return { status: w.status as OutreachRowStatus, outreachState: w.outreach_state };
}

function toRowEmail(w: WireRowEmail): OutreachRowEmail {
  return { address: w.address, source: w.source, confidence: w.confidence, foundAt: w.found_at, primary: w.primary };
}

/** A backend without the list (Go #695 not yet deployed) still has the creator's email: it is the
 *  one primary unless `email` is a campaign override, which is never the creator's own address. */
function toRowEmails(w: WireOutreachRow): OutreachRowEmail[] {
  if (w.emails) return w.emails.map(toRowEmail);
  if (!w.email || w.email_override) return [];
  return [{ address: w.email, source: w.email_source ?? '', confidence: 0, foundAt: undefined, primary: true }];
}

/** Go omits admin_tz when it is empty. */
export interface WireWaitingSend {
  mode: string;
  scheduled_for: string | null;
  admin_tz?: string;
  mailbox_id: number | null;
  chosen_at: string;
  expires_at: string;
}

/** A kind this build doesn't know is dropped rather than shown as something it isn't. */
function toRowFollowup(w: WireRowFollowup | null | undefined): OutreachFollowup | null {
  if (!w) return null;
  const { kind } = w;
  if (kind !== 'creator_wait' && kind !== 'no_reply' && kind !== 'steps_remaining') return null;
  const base = { dueAt: w.due_at, due: w.due, overdueDays: w.overdue_days ?? 0 };
  if (kind !== 'steps_remaining') return { ...base, kind };
  const failed = w.state === 'failed' ? { reason: w.reason, retryable: w.retryable, templateStep: w.template_step, attempts: w.attempts } : {};
  return { ...base, kind, nextAt: w.next_at ?? w.due_at, step: w.step, state: w.state, ...failed };
}

/** A mode this build doesn't know is dropped, so the row reads as a plain draft rather than Scheduled. */
function toWaitingSend(w: WireOutreachRow['waiting_send']): OutreachWaitingSend | null {
  if (!w) return null;
  const { mode } = w;
  if (mode !== 'now' && mode !== 'scheduled') return null;
  return {
    mode,
    ...(w.scheduled_for ? { scheduledFor: w.scheduled_for } : {}),
    ...(w.admin_tz ? { adminTz: w.admin_tz } : {}),
    ...(w.mailbox_id != null ? { mailboxId: w.mailbox_id } : {}),
    chosenAt: w.chosen_at,
    expiresAt: w.expires_at,
  };
}

/** Keeps Go's null ("the profile knows neither") apart from an absent field (an older Go). */
function toProfileGeo(g: WireProfileGeo | null | undefined): OutreachProfileGeo | null | undefined {
  if (g === undefined || g === null) return g;
  return { country: g.country, countrySource: g.country_source, timezone: g.timezone, timezoneSource: g.timezone_source };
}

export function toOutreachRow(w: WireOutreachRow): OutreachRow {
  const { status, outreachState } = validatedStatus(w);
  return {
    entryId: w.entry_id,
    userId: w.user_id,
    username: w.username,
    displayName: w.display_name,
    avatarUrl: w.avatar_url,
    followers: w.followers,
    followersHidden: w.followers_hidden,
    avgViews: w.avg_views,
    engagementRate: w.engagement_rate,
    lookupStatus: w.lookup_status,
    lookupCheckedAt: w.lookup_checked_at,
    metricsStatus: w.metrics_status,
    metricsPendingSince: w.metrics_pending_since,
    status,
    outreachState,
    outreachContact: contactOutcome(w.outreach_contact?.status),
    firstTouch: firstTouch(w.first_touch),
    email: w.email,
    emailSource: w.email_source,
    emailOverride: w.email_override,
    emails: toRowEmails(w),
    primaryPinned: w.primary_pinned ?? false,
    hasContact: w.has_contact,
    sendTz: w.send_tz,
    profileGeo: toProfileGeo(w.profile_geo),
    scheduledAt: w.scheduled_at,
    lastActivityAt: w.last_activity_at,
    syncing: w.syncing,
    syncStuck: w.sync_stuck ?? false,
    pinnedMailbox: w.pinned_mailbox ? toRowMailbox(w.pinned_mailbox) : undefined,
    nextSendAt: w.next_send_at,
    nextSendZone: w.next_send_zone,
    threadTz: w.thread_tz,
    nextFollowupAt: w.next_followup_at,
    lastMessage: w.last_message ? toRowLastMessage(w.last_message) : undefined,
    lastReply: w.last_reply ? { id: w.last_reply.id, receivedAt: w.last_reply.received_at, intent: w.last_reply.intent, snippet: w.last_reply.snippet, isAutoReply: w.last_reply.is_auto_reply } : undefined,
    openQuoteIntent: w.open_quote_intent ?? undefined,
    needsYou: w.needs_you ?? false,
    awaitingReply: w.awaiting_reply ?? false,
    escalatedAt: w.escalated_at,
    escalationReason: w.escalation_reason,
    followupsPausedAt: w.followups_paused_at,
    unread: w.unread ?? false,
    importSource: w.import_source as OutreachImportSource | undefined,
    agreedDeliverables: w.agreed_deliverables?.map((d) => ({
      type: d.type,
      basePrice: d.base_price,
      ...(d.currency ? { currency: d.currency } : {}),
      ...(d.converted ? { converted: d.converted } : {}),
    })),
    agreedMoney: w.agreed_money ?? undefined,
    platform: w.platform || 'instagram',
    contactLookupSupported: w.contact_lookup_supported ?? true,
    contactLookupNote: w.contact_lookup_note,
    draftWaitingEmail: w.draft_waiting_email ?? false,
    waitingSend: toWaitingSend(w.waiting_send),
    suppressed: w.suppressed ?? false,
    suppressionScope: w.suppression_scope,
    suppressionId: w.suppression_id,
    notUnsubscribe: w.not_unsubscribe,
    followupDeferred: w.followup_deferred
      ? { action: w.followup_deferred.action, reason: w.followup_deferred.reason, notBefore: w.followup_deferred.not_before, scheduledAt: w.followup_deferred.scheduled_at }
      : null,
    followup: toRowFollowup(w.followup),
    escalationSource: w.escalation_source,
    escalationNote: w.escalation_note,
    escalationClearedBy: w.escalation_cleared_by,
    escalationClearedAt: w.escalation_cleared_at,
    escalationClearedNote: w.escalation_cleared_note,
    heldReason: w.held_reason ?? null,
    holdReason: w.hold_reason ?? null,
    brandState: w.brand_state ?? 'none',
    handedOffAt: w.handed_off_at,
    brandDecidedAt: w.brand_decided_at,
    confirmedAt: w.confirmed_at,
    lastOutboundStatus: w.last_outbound_status || undefined,
    lastOutboundMessageId: w.last_outbound_message_id ?? undefined,
    messageCount: countOrNull(w.message_count),
    audienceSource: isOutreachAudienceSource(w.audience_source) ? w.audience_source : null,
    hasQuote: booleanOrNull(w.has_quote),
  };
}

export function toOutreachCounts(w: WireOutreachCounts): OutreachCounts {
  // A payload without the per-state counts (an older Go build) or with a state this build does not know would otherwise
  // read as zeros on every tab pill while the table lists rows: refuse it the same way a row is refused.
  if (!w.by_state || typeof w.by_state !== 'object') throw new OutreachWireError('by_state', w.by_state, 'counts');
  const unknown = Object.keys(w.by_state).find((key) => !isOutreachState(key));
  if (unknown !== undefined) throw new OutreachWireError('by_state', unknown, 'counts');
  // Half of the Removed badge: a missing value would undercount that tab silently, so refuse it like by_state.
  if (typeof w.removed_or_rejected !== 'number') throw new OutreachWireError('removed_or_rejected', w.removed_or_rejected, 'counts');
  const byState = w.by_state;
  return {
    byState: OUTREACH_STATES.reduce((acc, state) => ({ ...acc, [state]: byState[state] ?? 0 }), {} as Record<OutreachState, number>),
    withBrand: w.with_brand ?? 0,
    removedOrRejected: w.removed_or_rejected,
    scheduled: w.scheduled ?? 0,
    unread: w.unread ?? 0,
    ...(typeof w.not_contacted === 'number' ? { notContacted: w.not_contacted } : {}),
    ...(typeof w.in_conversation === 'number' ? { inConversation: w.in_conversation } : {}),
    conversations: countOrNull(w.conversations),
    withRates: countOrNull(w.with_rates),
    everyone: countOrNull(w.everyone),
    needsYou: typeof w.needs_you === 'number' ? w.needs_you : 0,
  };
}

/** `items` is ImportGet's own `items[]` on the same response as `batch` —
 *  optional because every OTHER caller of this mapper (the SSE
 *  outreach_import_done handler, which carries only batch-level counts) has
 *  none to give; createdEntryIds is simply empty in that case. */
export function toOutreachImportBatch(w: WireImportBatchView, items?: WireImportItemView[]): OutreachImportBatch {
  return {
    id: w.id,
    status: w.status as OutreachImportBatch['status'],
    rowCount: w.row_count,
    createdCount: w.created_count,
    matchedCount: w.matched_count,
    skippedCount: w.skipped_count,
    failedCount: w.failed_count,
    createdEntryIds: (items ?? [])
      .filter((it) => (it.state === 'created' || it.state === 'matched') && it.roster_entry_id)
      .map((it) => it.roster_entry_id as string),
  };
}

export function toOutreachOperation(w: WireOutreachOperation): OutreachOperation {
  return {
    id: w.operation_id,
    kind: w.kind,
    status: w.status as OutreachOperation['status'],
    total: w.total,
    appliedCount: w.applied_count,
    skippedCount: w.skipped_count,
    failedCount: w.failed_count,
    outcomes: w.outcomes.map((o) => ({
      itemKey: o.item_key,
      outcome: o.outcome,
      reason: o.reason,
      messageId: o.message_id,
      // Go omits retryable:false, so a failed draft_initial item without it is not retryable.
      retryable: w.kind === 'draft_initial' && o.outcome === 'failed' ? o.retryable === true : o.retryable,
    })),
  };
}

// ── F2 wizard ─────────────────────────────────────────────────────────────
export interface WireActiveOperation {
  operation_id: string;
  kind: string;
  status: string;
  total: number;
  applied_count: number;
  skipped_count: number;
  failed_count: number;
}

export function toActiveOperation(w: WireActiveOperation): OutreachActiveOperation {
  return {
    id: w.operation_id,
    kind: w.kind,
    status: w.status,
    total: w.total,
    appliedCount: w.applied_count,
    skippedCount: w.skipped_count,
    failedCount: w.failed_count,
  };
}
export interface WireBulkSend {
  batch_id: string;
  started_by: string;
  started_at: string;
  template_id?: number;
  template_name?: string;
  total: number;
  generated: number;
  skipped: number;
  failed: number;
  not_yet: number;
  running: boolean;
  ready: boolean;
  replaced?: boolean;
  live_drafts?: number;
  draft_floor: number;
  entries?: { entry_id: string; username: string; display_name?: string }[];
}

export interface WireFirstTouchDrafts {
  drafts: { message: WireMessage; current: boolean; edited: boolean; written_by: string }[] | null;
  frozen: boolean;
}

export function toFirstTouchDrafts(w: WireFirstTouchDrafts): OutreachFirstTouchDrafts {
  return {
    drafts: (w.drafts ?? []).map((d) => ({ message: toMessage(d.message), current: d.current, edited: d.edited, writtenBy: d.written_by === 'you' ? 'you' : 'assistant' })),
    frozen: w.frozen,
  };
}

export function toBulkSend(w: WireBulkSend): OutreachBulkSend {
  return {
    batchId: w.batch_id,
    startedBy: w.started_by,
    startedAt: w.started_at,
    templateId: w.template_id,
    templateName: w.template_name,
    total: w.total,
    generated: w.generated,
    skipped: w.skipped,
    failed: w.failed,
    notYet: w.not_yet,
    running: w.running,
    ready: w.ready,
    replaced: w.replaced ?? false,
    liveDrafts: w.live_drafts ?? 0,
    draftFloor: w.draft_floor,
    entries: w.entries?.map((e) => ({ entryId: e.entry_id, username: e.username, displayName: e.display_name })),
  };
}
// ── end F2 ────────────────────────────────────────────────────────────────

export function toTemplateStep(w: WireTemplateStep): OutreachTemplateStep {
  return {
    id: w.id,
    stepNo: w.step_no,
    delayDays: w.delay_days,
    subject: w.subject,
    body: w.body,
    ...(w.body_html ? { bodyHtml: w.body_html } : {}),
    ...(w.send_time ? { sendTime: w.send_time } : {}),
    aiAssisted: w.ai_assisted ?? false,
    ...(w.manual ? { manual: true } : {}),
  };
}

export function toTemplate(w: WireTemplate): OutreachTemplate {
  return {
    id: w.id,
    scope: w.scope,
    brandId: w.brand_id,
    campaignId: w.campaign_id,
    parentTemplateId: w.parent_template_id,
    kind: w.kind,
    name: w.name,
    steps: (w.steps ?? []).map(toTemplateStep),
    usedBy: w.used_by,
    archivedAt: w.archived_at,
    createdBy: w.created_by,
    updatedAt: w.updated_at,
    version: w.version,
    aiAssisted: w.ai_assisted ?? false,
    signature: w.signature ?? null,
    ...(w.signature_html ? { signatureHtml: w.signature_html } : {}),
    warnings: w.warnings?.map((x): OutreachTemplateLintWarning => ({ stepNo: x.step_no, code: x.code, message: x.message })),
  };
}

export function fromTemplateStep(s: OutreachTemplateStep): WireTemplateStep {
  return {
    step_no: s.stepNo,
    delay_days: s.delayDays,
    subject: s.subject,
    body: s.body,
    body_html: s.bodyHtml,
    ...(s.sendTime ? { send_time: s.sendTime } : {}),
    ai_assisted: s.aiAssisted ?? false,
  };
}

export interface WireFillStepResult {
  subject: string | null;
  body_html: string;
  signed_off: boolean;
  warnings?: string[] | null;
}

export function toFillStepResult(w: WireFillStepResult): OutreachFillStepResult {
  return { subject: w.subject ?? null, bodyHtml: w.body_html, signedOff: w.signed_off, warnings: w.warnings ?? [] };
}

export function toTemplatePreview(w: WireTemplatePreview): OutreachTemplatePreview {
  return {
    subject: w.subject,
    bodyText: w.body_text,
    ...(w.body_html ? { bodyHtml: w.body_html } : {}),
    ...(w.signature_source ? { signatureSource: w.signature_source } : {}),
  };
}

export function toSettings(w: WireSettings): OutreachSettings {
  return {
    campaignId: w.campaign_id,
    exists: w.exists,
    sequenceTemplateId: w.sequence_template_id,
    askRateFirst: w.ask_rate_first,
    sendTz: w.send_tz,
    walkAwayCeiling: w.walk_away_ceiling,
    maxRounds: w.max_rounds,
    followupDailyCap: w.followup_daily_cap,
    autoFollowups: w.auto_followups ?? true,
    region: w.region ? w.region.toUpperCase() : DEFAULT_OUTREACH_REGION,
    regionSet: w.region_set ?? false,
    pausedAt: w.paused_at,
    updatedAt: w.updated_at,
    templateLocked: w.template_locked ?? false,
  };
}

// The BE's PutInput has no omitempty on the pointer fields: a missing
// sequence_template_id/walk_away_ceiling means "clear it", so both are sent
// explicitly as null rather than dropped.
export function fromSettings(input: OutreachSettingsInput): Record<string, unknown> {
  return {
    sequence_template_id: input.sequenceTemplateId ?? null,
    ask_rate_first: input.askRateFirst,
    send_tz: input.sendTz,
    walk_away_ceiling: input.walkAwayCeiling ?? null,
    max_rounds: input.maxRounds,
    followup_daily_cap: input.followupDailyCap,
    ...(input.autoFollowups === undefined ? {} : { auto_followups: input.autoFollowups }),
    ...(input.region === undefined ? {} : { region: input.region }),
    ...(input.ifUnmodifiedSince ? { if_unmodified_since: input.ifUnmodifiedSince } : {}),
  };
}

const CHIP_ACTIONS: readonly OutreachReplyChipAction[] = ['confirm_rate', 'ask_for_rate', 'answer_question', 'counter', 'reply'];

function toChips(list: unknown): OutreachReplyChip[] {
  if (!Array.isArray(list)) return [];
  const out: OutreachReplyChip[] = [];
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') continue;
    const c = raw as { action?: unknown; label?: unknown; source?: unknown; draft?: { subject?: unknown; body?: unknown } | null };
    // CHIP_ACTIONS must list every OutreachReplyChipAction (a typed array
    // rejects a stray value, not a missing one); the membership check itself
    // still needs to accept an arbitrary wire string before narrowing it.
    if (typeof c.action !== 'string' || !(CHIP_ACTIONS as readonly string[]).includes(c.action) || (c.source !== 'llm' && c.source !== 'template')) continue;
    if (typeof c.label !== 'string' || !c.label.trim() || typeof c.draft?.body !== 'string' || !c.draft.body.trim()) continue;
    const subject = typeof c.draft.subject === 'string' ? c.draft.subject : null;
    out.push({ action: c.action as OutreachReplyChipAction, label: c.label, draft: { subject, body: c.draft.body }, source: c.source });
  }
  return out;
}

function toBundleDetail(b: NonNullable<WireParsed['bundle_detail']>): OutreachParsedBundle {
  const items = (Array.isArray(b.items) ? b.items : [])
    .filter((i) => i && typeof i.deliverable_id === 'string' && Number.isFinite(i.count) && i.count > 0)
    .map((i) => ({ deliverableId: i.deliverable_id, count: i.count, ...(typeof i.amount === 'number' ? { amount: i.amount } : {}) }));
  return {
    deliverableIds: b.deliverable_ids,
    amount: b.amount,
    currency: b.currency,
    ...(b.per_unit ? { perUnit: true } : {}),
    ...(items.length ? { items } : {}),
    ...(Array.isArray(b.free_ids) && b.free_ids.length ? { freeIds: b.free_ids } : {}),
  };
}

/** Defensive on purpose: a reply parsed before chips existed has none of the new keys, and one malformed element must never break the whole thread. */
export function toParsed(w: WireParsed): OutreachParsed {
  const bundle = w.bundle_detail;
  return {
    source: w.source,
    rates: (Array.isArray(w.rates) ? w.rates : []).map((r) => ({
      deliverable: r.deliverable,
      amount: r.amount,
      currency: r.currency,
      confidence: typeof r.confidence === 'number' ? r.confidence : 0,
      quote: r.quote,
      ...(r.not_pitched ? { notPitched: true } : {}),
    })),
    unmatched: (Array.isArray(w.unmatched) ? w.unmatched : []).map((u) => ({ amount: u.amount, currency: u.currency, quote: typeof u.quote === 'string' ? u.quote : '' })),
    bundle: w.bundle,
    bundleDetail: bundle && Array.isArray(bundle.deliverable_ids) ? toBundleDetail(bundle) : undefined,
    phone: w.phone,
    notes: w.notes,
    suggestions: toChips(w.suggestions),
  };
}

export function toMessage(w: WireMessage): OutreachMessage {
  return {
    id: w.id,
    direction: w.direction,
    kind: w.kind,
    step: w.step,
    status: w.status,
    subject: w.subject,
    bodyText: w.body_text,
    toAddress: w.to_address,
    fromAddress: w.from_address,
    mailboxId: w.mailbox_id,
    requestedMailboxId: w.requested_mailbox_id,
    heldReason: w.held_reason,
    ...(w.address_guard ? { addressGuard: w.address_guard } : {}),
    lastError: w.last_error,
    ...(w.draft_returned ? { draftReturned: true } : {}),
    scheduledFor: w.scheduled_for,
    sendTz: w.send_tz,
    sendAttemptedAt: w.send_attempted_at,
    sentAt: w.sent_at,
    receivedAt: w.received_at,
    visibleText: w.visible_text,
    intent: w.intent,
    parsed: w.parsed ? toParsed(w.parsed) : undefined,
    matchConfidence: w.match_confidence,
    candidateEntryId: w.candidate_entry_id,
    isAutoReply: w.is_auto_reply ?? false,
    isDsn: w.is_dsn ?? false,
    dsn: w.dsn ? { action: w.dsn.action, status: w.dsn.status, kind: w.dsn.kind, originalMessageId: w.dsn.original_message_id } : undefined,
    truncated: w.truncated ?? false,
    parseError: w.parse_error,
    quote: w.quote
      ? { id: w.quote.id, round: w.quote.round, humanRound: w.quote.human_round, rates: w.quote.rates.map((rate) => ({ ...rate })), createdAt: w.quote.created_at }
      : undefined,
    bodyHtml: w.body_html,
    bodyRichHtml: w.body_rich_html,
    attempts: w.attempts,
    actor: w.actor,
    createdAt: w.created_at,
    attachments: w.attachments?.map((a) => ({ id: a.id, filename: a.filename, contentType: a.content_type, sizeBytes: a.size_bytes, isInline: a.is_inline === true })),
    // 'fallback' is on historical rows (migration 0048): template text.
    draftSource: w.draft_source === 'fallback' ? 'template' : (w.draft_source ?? null),
    editedBeforeApproval: w.edited_before_approval === true,
  };
}

export function toQueuedMessage(w: WireQueuedMessage): OutreachQueuedMessage {
  return { messageId: w.message_id, status: w.status, scheduledFor: w.scheduled_for };
}

export function toPoolMailbox(w: WireMailboxView): OutreachPoolMailbox {
  return {
    id: w.id,
    address: w.address,
    displayName: w.display_name,
    status: w.status,
    sentToday: w.sent_today,
    capToday: w.cap_today,
  };
}

export function toSendStatus(w: WireSendStatus): OutreachSendStatus {
  return {
    enabled: w.enabled,
    sendHold: w.send_hold,
    gmailConfigured: w.gmail_configured,
    mailboxCount: w.mailbox_count,
    activeMailboxes: w.active_mailboxes,
    unmatchedCount: w.unmatched_count ?? 0,
  };
}

export function toWireContactPatch(patch: OutreachContactPatch): Record<string, unknown> {
  const wire: Record<string, unknown> = {};
  if ('email' in patch) wire.email = patch.email;
  if ('primaryEmail' in patch) wire.primary_email = patch.primaryEmail;
  if ('phone' in patch) wire.phone = patch.phone;
  if ('whatsapp' in patch) wire.whatsapp = patch.whatsapp;
  if ('emailOverride' in patch) wire.email_override = patch.emailOverride;
  if ('sendTz' in patch) wire.send_tz = patch.sendTz;
  return wire;
}

// ── Negotiation, drafts and KPIs (Outreach v2 Part 2.5) ────────────────────

/** ruling 46's DraftView{message, entry} — one drafts-queue row. */
export interface WireDraft {
  message: WireMessage;
  entry: WireOutreachRow;
}

export function toDraft(w: WireDraft): OutreachDraft {
  const message = toMessage(w.message);
  return {
    message,
    entry: toOutreachRow(w.entry),
    kind: message.kind === 'initial' ? 'initial' : 'followup',
    draftSource: message.draftSource ?? null,
  };
}

export interface WireSuggestion {
  subject: string;
  body: string;
  body_html?: string;
  job_id?: string;
}

export function toSuggestion(w: WireSuggestion): OutreachSuggestion {
  return { subject: w.subject, body: w.body, ...(w.body_html ? { bodyHtml: w.body_html } : {}), jobId: w.job_id };
}

export interface WireTemplateStepText {
  step: number;
  steps_total?: number;
  subject?: string | null;
  body: string;
  body_html?: string;
  draft_source?: string;
}

export function toTemplateStepText(w: WireTemplateStepText): OutreachTemplateStepText {
  return { step: w.step, ...(w.subject ? { subject: w.subject } : {}), body: w.body, ...(w.body_html ? { bodyHtml: w.body_html } : {}) };
}

export { toKpis, type WireFunnelSteps, type WireKindRates, type WireKpis, type WireStageMix } from './outreachWireKpis';
export {
  toMailboxHealth,
  toRatesPage,
  type WireMailboxHealth,
  type WireMailboxHealthItem,
  type WireMoney,
  type WireRateFormat,
  type WireRatesPage,
  type WireRatesRow,
} from './outreachWireCrm';
